/**
 * Element id of the shared gtag.js loader.
 *
 * GA4 and Google Ads both need gtag.js and either may be the one to load it, so
 * they inject under a common id to keep it to a single copy. Whichever id ends
 * up in the script URL only bootstraps the library; every account is addressed
 * by its own `config` call regardless.
 */
export const GTAG_SCRIPT_ID = 'gtag-js';

/**
 * `window` augmented with the tracking globals the head pixels read and write.
 * Shared so GA4 and Google Ads use one consistent `dataLayer` contract instead
 * of each casting `window` to its own ad-hoc shape.
 */
export type TrackingWindow = Window &
    typeof globalThis & {
        dataLayer?: unknown[];
        gtag?: (...args: unknown[]) => void;
    };

export const getTrackingWindow = (): TrackingWindow => window as TrackingWindow;

/**
 * Return `window.gtag`, creating the canonical stub if it does not exist yet.
 * The stub pushes the raw `arguments` object onto `dataLayer` — the exact shape
 * gtag.js expects for commands (`js`, `config`, `consent`) queued before it has
 * loaded. `dataLayer` is gtag's transport, not a Tag Manager artefact.
 */
export const getOrCreateGtag = (
    w: TrackingWindow,
): NonNullable<TrackingWindow['gtag']> => {
    w.dataLayer = w.dataLayer || [];
    if (!w.gtag) {
        w.gtag = function gtagStub() {
            // eslint-disable-next-line prefer-rest-params
            (w.dataLayer as unknown[]).push(arguments);
        } as TrackingWindow['gtag'];
    }
    return w.gtag;
};

/**
 * Append a third-party `<script src>` to `<head>` once, on the client.
 *
 * Head pixels must not be server-rendered into `<head>`: their bootstraps mutate
 * `<head>` during the parse/hydration window, shifting the nodes React hydrates and
 * breaking document hydration. Injecting after hydration via `createElement` +
 * `appendChild` keeps the hydrated `<head>` untouched and still runs the script — a
 * client-rendered `dangerouslySetInnerHTML` script would not execute.
 *
 * Idempotent: returns `false` without re-injecting when a script with the same `id`
 * (or the same `src`, if no `id` is given) is already present, so a caller can gate
 * one-time bootstrap side effects on the return value.
 */
export const injectHeadScript = (src: string, id?: string): boolean => {
    const existing = id
        ? document.getElementById(id)
        : document.querySelector(`script[src="${src}"]`);
    if (existing) return false;

    const script = document.createElement('script');
    if (id) script.id = id;
    script.async = true;
    script.src = src;
    document.head.appendChild(script);
    return true;
};
